/**
 * AI CONTEXT
 * Purpose: core data engine for admin/super views (sessions/classes/projects/tasks/date selection).
 * Handles: date-scoped session query, cross-doc joins, student task formatting, readiness and error surfacing.
 * Exposes: processedSessions, studentTasks, selectedDate/setSelectedDate.
 * Used by: AdminViewContextProvider.
 * Refactor: split into domain hooks (date/session join, task formatting, error reporting).
 */
import dayjs from "dayjs"
import { isEmpty, keyBy, orderBy, uniq } from "lodash"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
  classAtoms,
  classSessionAtoms,
  makeupAtoms,
  projectAtoms,
} from "#atoms"
import { Student } from "#classes/Student"
import { CLASS_GROUPS, ROOM_COLORS, ROOMS } from "#constants/classOptions"
import { USER_SETTINGS } from "#constants/globalConstants"
import { Hexcode } from "#contexts/ThemeContextProvider"
import { useUserContext } from "#contexts/UserContextProvider"
import { ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import {
  COMPARATORS,
  DOC_TYPES,
  FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import { MakeupConstructor } from "#customTypes/composedTypes/makeupTypes"
import { ProjectConstructor } from "#customTypes/composedTypes/projectTypes"
import { StudentTaskCardData } from "#customTypes/composedTypes/studentTaskTypes"
import { Uuid } from "#customTypes/globalTypes"
import { Dayjs } from "#customTypes/types"
import {
  useIsAllFulfilled,
  useIsFulfilled,
} from "#hooks/jotai/useCascadingData"
import { useGetSomeStudents } from "#hooks/jotai/useGetStudents"
import {
  useFilteredDocs,
  useSomeDocs,
  useStableIds,
} from "#hooks/jotai/useSubscription"
import useErrorModal from "#hooks/useErrorModal"
import { isDefined } from "#utils/classHelpers"
import {
  getNow,
  isToday,
  STANDARD_DATE_FORMAT,
  STANDARD_DATE_TIME_FORMAT,
} from "#utils/dateHelpers"
import { getIsAllTasksCompleted, isTaskUrgent } from "#utils/taskHelpers"
import { ErrorType } from "#utils/textFormatters"

import { useGetSomeUsers } from "./useGetUser"

// Generate a stable key for an error to dedupe
const getErrorSignature = (errors: ErrorType[], context: string): string =>
  `${context}::${errors.map((e) => String(e)).join("|")}`

export type AdminViewClassSession = {
  id: Uuid
  classGroup: CLASS_GROUPS
  startTime: Dayjs
  endTime: Dayjs
  name: string
  room: ROOMS
  color: Hexcode
  class: ClassConstructor
  session: ClassSessionConstructor
  project: ProjectConstructor | undefined
  notes: string | undefined
}

/** Resolve a saved date string to either "TODAY" sentinel or a valid date string */
const resolveInitialDateValue = (saved: string | null): "TODAY" | string => {
  if (!saved || saved === "TODAY") return "TODAY"
  const parsed = dayjs(saved, STANDARD_DATE_FORMAT)
  if (!parsed.isValid()) return "TODAY"
  if (isToday(parsed, getNow())) return "TODAY"
  return saved
}

const useAdminViewData = () => {
  const { getUserSetting, setUserSetting } = useUserContext()
  const openErrorModal = useErrorModal("useAdminViewData")

  // Track which error signatures have already been shown to avoid repeat modals
  const shownErrorSignatures = useRef<Set<string>>(new Set())

  const savedDateString = getUserSetting<string | null>(
    USER_SETTINGS.SELECTED_DATE
  )

  // Once the user interacts with the date picker, stop syncing from user settings
  // to avoid jitter from the async Firestore round-trip overwriting local state
  const hasUserInteracted = useRef(false)

  // "TODAY" sentinel resolves to getNow() on render; otherwise a formatted date string
  const [dateValue, setDateValue] = useState<"TODAY" | string>(() =>
    resolveInitialDateValue(savedDateString)
  )

  // Sync from user settings until the user first interacts (handles async currentUser load)
  useEffect(() => {
    if (hasUserInteracted.current) return
    setDateValue(resolveInitialDateValue(savedDateString))
  }, [savedDateString])

  // Derive the actual Dayjs value — "TODAY" always resolves to the current getNow()
  const selectedDate = useMemo(() => {
    if (dateValue === "TODAY") return getNow()
    const parsed = dayjs(dateValue, STANDARD_DATE_FORMAT)
    return parsed.isValid() ? parsed : getNow()
  }, [dateValue])

  const setSelectedDate = useCallback(
    (date: Dayjs) => {
      hasUserInteracted.current = true
      const isTodayDate = isToday(date, getNow())
      setDateValue(isTodayDate ? "TODAY" : date.format(STANDARD_DATE_FORMAT))
      setUserSetting(
        USER_SETTINGS.SELECTED_DATE,
        isTodayDate ? "TODAY" : date.format(STANDARD_DATE_FORMAT)
      )
    },
    [setUserSetting]
  )

  const queryConfig: FirebaseQueryConfig = useMemo(() => {
    const startOfDay = dayjs(selectedDate).startOf("day").toISOString()
    const endOfDay = dayjs(selectedDate).endOf("day").toISOString()

    return {
      queries: {
        eventStart: {
          comparator: COMPARATORS.GREATER_OR_EQUALS,
          value: startOfDay,
        },
        eventEnd: {
          comparator: COMPARATORS.LESS_OR_EQUALS,
          value: endOfDay,
        },
      },
    }
  }, [selectedDate])

  const sessionsFromDb = useFilteredDocs<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    queryConfig
  )

  const projectIds = useMemo(() => {
    if (!sessionsFromDb?.length) return []

    const projectIds = sessionsFromDb
      .map((session) => session.projectId)
      .filter((id): id is Uuid => Boolean(id))

    return uniq(projectIds)
  }, [sessionsFromDb])
  const stableProjectIds = useStableIds(projectIds)

  const projectsFromDb = useSomeDocs<ProjectConstructor>(
    projectAtoms,
    DOC_TYPES.PROJECTS,
    stableProjectIds
  )

  const projectsById = useMemo(
    () => keyBy(projectsFromDb, "id"),
    [projectsFromDb]
  )

  const classIds = useMemo(() => {
    if (!sessionsFromDb?.length) return []

    const classIds = sessionsFromDb
      .map((session) => session.classId)
      .filter((id): id is Uuid => Boolean(id))
    return uniq(classIds)
  }, [sessionsFromDb])
  const stableClassIds = useStableIds(classIds)

  const sessionStudentIds = useMemo(() => {
    if (!sessionsFromDb?.length) return []

    return uniq(
      sessionsFromDb?.flatMap((session) => session.studentIds ?? []) ?? []
    )
  }, [sessionsFromDb])

  const sessionMakeupIds = useMemo(() => {
    if (!sessionsFromDb?.length) return []

    return uniq(
      sessionsFromDb?.flatMap((session) => session.makeupIds ?? []) ?? []
    )
  }, [sessionsFromDb])
  const stableSessionMakeupIds = useStableIds(sessionMakeupIds)

  const makeupsFromDb = useSomeDocs<MakeupConstructor>(
    makeupAtoms,
    DOC_TYPES.MAKEUPS,
    stableSessionMakeupIds
  )

  const makeupStudentIds = useMemo(() => {
    if (!makeupsFromDb?.length) return []

    return uniq(
      makeupsFromDb
        .map((makeup) => makeup.studentId)
        .filter((id): id is Uuid => Boolean(id))
    )
  }, [makeupsFromDb])

  const allSessionStudentIds = useMemo(() => {
    return uniq([...sessionStudentIds, ...makeupStudentIds])
  }, [sessionStudentIds, makeupStudentIds])
  const stableSessionStudentIds = useStableIds(allSessionStudentIds)

  const sessionStudents = useGetSomeStudents(stableSessionStudentIds)

  // Map each student id to the sessions they actually have today.
  // Includes both regular enrollment and makeups — makeup students don't have
  // attendance entries for the makeup class, so we can't infer this from
  // student.attendance alone.
  const todaySessionsByStudentId = useMemo(() => {
    const result: Record<Uuid, ClassSessionConstructor[]> = {}
    if (!sessionsFromDb?.length) return result

    sessionsFromDb.forEach((session) => {
      session.studentIds?.forEach((studentId) => {
        if (!result[studentId]) result[studentId] = []
        result[studentId].push(session)
      })
    })

    if (makeupsFromDb?.length) {
      const sessionsById = keyBy(sessionsFromDb, "id") as Record<
        Uuid,
        ClassSessionConstructor
      >

      makeupsFromDb.forEach((makeup) => {
        if (!makeup.makeupSessionId) return
        const session = sessionsById[makeup.makeupSessionId]
        if (!session) return
        const list = result[makeup.studentId] ?? []
        if (!list.some((s) => s.id === session.id)) list.push(session)
        result[makeup.studentId] = list
      })
    }

    return result
  }, [sessionsFromDb, makeupsFromDb])

  // Get all the user ids that created or completed a student task
  const studentTaskCreatedByIds: Uuid[] = useMemo(() => {
    if (!sessionStudents?.length) return []

    const allIds = sessionStudents.flatMap(
      (student) =>
        student.tasks?.flatMap(({ createdBy, completedBy }) => [
          createdBy,
          completedBy,
        ]) ?? []
    )

    return uniq(allIds).filter((id): id is Uuid => Boolean(id))
  }, [sessionStudents])

  // Get all the users that created or completed a student task
  const studentTaskUsers = useGetSomeUsers(studentTaskCreatedByIds)

  const studentTaskUserNamesById = useMemo(() => {
    if (!studentTaskUsers) return {}

    const userNamesById: Record<Uuid, string> = {}
    studentTaskUsers.forEach((user) => (userNamesById[user.id] = user.name))
    return userNamesById
  }, [studentTaskUsers])

  // Format the student tasks
  const { studentTasks, studentTaskErrors } = useMemo(() => {
    if (!sessionStudents?.length || !studentTaskUserNamesById)
      return {
        studentTasks: [] as StudentTaskCardData[],
        studentTaskErrors: [] as ErrorType[],
      }

    const errors: ErrorType[] = []

    const formattedTasks = sessionStudents
      ?.filter((student) => {
        const hasTasks = student.tasks?.length

        if (!hasTasks) return false

        const hasUncompletedTasks = student.tasks?.some(
          (task) => !task.completedAt
        )

        if (hasUncompletedTasks) return true

        const today = getNow()
        const hasTasksCompletedEarlierToday = student.tasks?.some((task) => {
          const taskCompletedAt = dayjs(task.completedAt)
          return isToday(taskCompletedAt, today)
        })

        // If the task list was completed earlier today, we should show the student
        return !hasUncompletedTasks && hasTasksCompletedEarlierToday
      })
      .map((student) => {
        try {
          const formattedTasks = student.tasks?.map((task) => ({
            ...task,
            createdAt: dayjs(task.createdAt).format(STANDARD_DATE_TIME_FORMAT),
            createdBy: studentTaskUserNamesById[task.createdBy],
            completedBy: task.completedBy
              ? studentTaskUserNamesById[task.completedBy]
              : null,
            completedAt: task.completedAt
              ? dayjs(task.completedAt).format(STANDARD_DATE_TIME_FORMAT)
              : null,
          }))

          return {
            id: student.id, // Used as a list key
            studentId: student.id,
            header: new Student(student).getNameAndDob(),
            // This is the main failure point, so we add to errors instead of throwing if it fails
            isUrgent: isTaskUrgent(
              student,
              todaySessionsByStudentId[student.id] ?? [],
              errors
            ),
            isAllTasksCompleted: getIsAllTasksCompleted(student.tasks),
            tasks: orderBy(
              formattedTasks,
              ["completedAt", "createdAt", "label"],
              ["desc", "asc", "asc"]
            ),
          }
        } catch (error) {
          errors.push(error as ErrorType)
        }
      })
      .filter(isDefined)

    return { studentTasks: formattedTasks, studentTaskErrors: errors }
  }, [sessionStudents, todaySessionsByStudentId, studentTaskUserNamesById])

  const classesFromDb = useSomeDocs<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    stableClassIds
  )

  const classesById = useMemo(
    () => keyBy(classesFromDb, "id") as Record<Uuid, ClassConstructor>,
    [classesFromDb]
  )

  // Track fulfillment
  const classesFulfilled = useIsFulfilled(stableClassIds, classesFromDb, 5000)
  const projectsFulfilled = useIsFulfilled(
    stableProjectIds,
    projectsFromDb,
    5000
  )
  const makeupsFulfilled = useIsFulfilled(
    stableSessionMakeupIds,
    makeupsFromDb,
    3000
  )

  const isReady = useIsAllFulfilled([
    classesFulfilled,
    projectsFulfilled,
    makeupsFulfilled,
  ])

  const { processedSessions, sessionProcessingErrors } = useMemo(() => {
    // This seems to happen when a new item is added (like a makeup) during the data fetch
    const doClassesMatchSessions =
      Object.keys(classesById).length === (sessionsFromDb?.length ?? 0)

    if (
      !sessionsFromDb?.length ||
      isEmpty(classesById) ||
      !doClassesMatchSessions ||
      !isReady
    )
      return {
        processedSessions: [],
        sessionProcessingErrors: [],
      }

    const errors: ErrorType[] = []

    const mappedSessions: AdminViewClassSession[] = sessionsFromDb
      .map((session) => {
        const typesafeSession = session as ClassSessionConstructor
        const classObj = classesById[typesafeSession.classId]

        if (!classObj) {
          errors.push(`Class data is missing for session ${typesafeSession.id}`)
          return null
        }

        return {
          id: typesafeSession.id,
          session: typesafeSession,
          class: classObj,
          classGroup: classObj.group,
          startTime: dayjs(typesafeSession.eventStart),
          endTime: dayjs(typesafeSession.eventEnd),
          name: classObj.name,
          room: classObj.room,
          color: ROOM_COLORS[classObj.room],
          project: session.projectId
            ? projectsById[session.projectId]
            : undefined,
          notes: classObj.notes,
        }
      })
      .filter((session) => session !== null)

    return {
      processedSessions: mappedSessions,
      sessionProcessingErrors: errors,
    }
  }, [sessionsFromDb, classesById, isReady, projectsById])

  useEffect(() => {
    const allErrors = [...studentTaskErrors, ...sessionProcessingErrors]
    if (allErrors.length === 0) return

    const signature = getErrorSignature(allErrors, "adminViewData")

    if (shownErrorSignatures.current.has(signature)) return

    shownErrorSignatures.current.add(signature)

    const context = `Today's session ids: ${sessionsFromDb?.map((s) => s.id).join(", ")}`
    openErrorModal(allErrors, context)
  }, [
    studentTaskErrors,
    sessionProcessingErrors,
    sessionsFromDb,
    openErrorModal,
  ])

  return {
    isReady,
    todaysSessions: sessionsFromDb,
    classesFromDb: classesFromDb,
    processedSessions,
    sessionStudents,
    studentTasks,
    selectedDate,
    setSelectedDate,
  }
}
export default useAdminViewData
